import { useCallback, useMemo, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FileUploadMetaDataExtractionDto } from '@asset-metrix/portal-gateway-client-js';
import {
  Button,
  ConditionalFilterPlugin,
  FetchOptionsFunction,
  FileData,
  fileReducer,
  FileSelection,
  FileSelectionState,
  GoogleFontIcon,
  InlineAlert,
  isFileSelection,
  jsonEditorWidgets,
  Modal,
  Options,
  SVGIcon,
  Upload,
} from '@asset-metrix/portal-ui';
import {
  JsonError,
  JsonSchema,
  useEditor,
  useEditorPlugin,
  Widget,
} from '@sagold/react-json-editor';
import clsx from 'clsx';

import { DOC_UPLOAD_WITH_FILE_METADATA_EXTRACTION_KEY } from '../../utils/constants';

import { RenderMetaDataExtractedView } from './RenderMetaDataExtractedView';
import { useGenerateSchemaDisablingMetaDataExtractionKey } from './useGenerateSchemaDisablingMetaDataExtractionKey';
import { useGenerateSchemaForMetaDataExtraction } from './useGenerateSchemaForMetaDataExtraction';
import { useGetUserFilteredData } from './useGetUserFilteredData';

import styles from './doc-upload-modal.module.scss';

export const MetaDataExtractionInfo = ({
  fileState,
  validateViewErrors,
}: {
  fileState: FileSelectionState;
  validateViewErrors: Record<string, JsonError[]>;
}) => {
  const { t } = useTranslation();

  const metaDataExtractionInfo = useMemo(() => {
    if (fileState.length === 0) {
      return null;
    }

    const dataMissing = Object.values(validateViewErrors).filter(
      (value) => Array.isArray(value) && value.length > 0
    ).length;

    if (!dataMissing) {
      return null;
    }
    const totalFiles = fileState.length;
    const success = totalFiles - dataMissing;

    return (
      <InlineAlert className={styles['warning']}>
        {t('app.upload.extractMetaData.totalFiles', '', {
          count: totalFiles,
          success: success,
        })}{' '}
        {t('app.upload.extractMetaData.failedToExtract', '', {
          count: dataMissing,
        })}
      </InlineAlert>
    );
  }, [fileState.length, t, validateViewErrors]);

  return <div>{metaDataExtractionInfo}</div>;
};

export type DocUploadModalComponentProps = {
  acceptedMimeTypes?: string[];
  contentId: string;
  documentMetaDataExtraction?: FileUploadMetaDataExtractionDto;
  fetchConditionalFilterOptions: FetchOptionsFunction;
  jsonSchema: JsonSchema;
  onClose: () => void;
  onUploadSuccess?: () => Promise<unknown>;
  uploadFile: (
    file: FileData,
    metadata: Record<string, unknown>
  ) => Promise<void>;
  withinPortal?: boolean;
};

export function DocUploadModalComponent({
  acceptedMimeTypes,
  contentId,
  documentMetaDataExtraction,
  fetchConditionalFilterOptions,
  jsonSchema,
  onClose,
  onUploadSuccess,
  uploadFile,
  withinPortal,
}: DocUploadModalComponentProps) {
  const { t } = useTranslation();
  const [fileState, dispatch] = useReducer(fileReducer, []);
  const [uploadViewErrors, setUploadViewErrors] = useState<JsonError[]>([]);
  const [validateViewErrors, setValidateViewErrors] = useState<
    Record<string, JsonError[]>
  >({});

  const [currentView, setCurrentView] = useState<'upload' | 'validate'>(
    'upload'
  );

  // maintain state if conditional-filter is fetching tableModalValues
  const [isTableModalValuesLoading, setIsTableModalValuesLoading] =
    useState(false);
  // maintain state to have latest cachedValue of tabelModalValue
  const [cachedTabelModalValue, setCachedTabelModalValue] = useState<
    { [p: string]: Options } | undefined
  >();

  // if metadata extraction is enabled this hook returns a new jsonSchema with metadata key disabled.
  // else it will return orignal jsonSchema
  const jsonSchemaWithoutMetaDataExtractionKey =
    useGenerateSchemaDisablingMetaDataExtractionKey(
      jsonSchema,
      documentMetaDataExtraction
    );

  // if metada extraction is enabled this hook return data extracted from file
  // & a new jsonSchema with only required key of metadata-extraction-key
  const docMetadataExtractionInfo = useGenerateSchemaForMetaDataExtraction(
    jsonSchema,
    cachedTabelModalValue,
    documentMetaDataExtraction
  );

  // we store data from the file-specific metadata screen,
  // as we cannot really use the editor ref here as user can go back in the screen where
  // the editor will be unmounted from component.
  // also as each file can have different metadata and we need to store each file-specific data.
  const [validateViewEditorData, setValidateViewEditorData] = useState<
    Record<string, Record<string, unknown>>
  >({});

  // state to maintain uploading errors.
  const [conditionalFilterOptionsError, setConditionalFilterOptionsError] =
    useState<string | undefined>(undefined);

  const setErrors = useCallback(
    (fieldId: string, error: JsonError[]) =>
      setValidateViewErrors((prev) => ({ ...prev, [fieldId]: error })),
    []
  );

  const isInitial = fileState.every(
    (selection) => selection.uploadState === 'initial'
  );

  const isPending = fileState.some(
    (selection) => selection.uploadState === 'isPending'
  );

  // In some cases, the "extraction" property is part of an "if" condition inside an "allOf" object in the JSON schema, expecting a minimum value.
  // However, since this property is hidden in the upload flow with empty value, the JSON schema validation fails, triggering an error in "editor.getErrors()".
  // These errors are used to disable the upload/check metadata button in the upload doc flow. This edge case causes the button to remain disabled, leaving the user unaware and stuck in the upload flow.
  // To address this, we ignore this specific error and allow the user to proceed with the upload flow.
  const filterMetaDataError = useCallback(
    (errors: JsonError[]) =>
      errors.filter(
        (error) => error.data.pointer !== `#/${documentMetaDataExtraction?.key}`
      ),
    [documentMetaDataExtraction]
  );
  // Initialize editor
  const initialData = useGetUserFilteredData(
    jsonSchemaWithoutMetaDataExtractionKey,
    contentId
  );
  const editor = useEditor({
    widgets: jsonEditorWidgets,
    schema: jsonSchemaWithoutMetaDataExtractionKey,
    onChange: (_data, _root, editor) => {
      setUploadViewErrors(filterMetaDataError(editor.getErrors()));
    },
    validate: true,
    data: initialData,
  });

  const fetchOptions = useCallback<FetchOptionsFunction>(
    async (filter) => {
      setConditionalFilterOptionsError(undefined);
      setIsTableModalValuesLoading(true);
      try {
        const result = await fetchConditionalFilterOptions(filter);
        setCachedTabelModalValue(result);
        return result;
      } catch (error) {
        setConditionalFilterOptionsError(
          t('app.upload.conditionalFilterOptionsError')
        );
        throw error;
      } finally {
        setIsTableModalValuesLoading(false);
      }
    },
    [fetchConditionalFilterOptions, t]
  );

  useEditorPlugin(editor, ConditionalFilterPlugin, { fetchOptions });

  const onMetaDataEditorChange = (
    fieldId: string,
    data: Record<string, unknown>
  ) => {
    if (Object.keys(data).length > 0) {
      const dynamicKey = Object.keys(data)[0];
      const value = data[dynamicKey] as [] | string;

      if (value || (Array.isArray(value) && value.length > 0)) {
        dispatch({
          type: 'updateFileMetaData',
          payload: { id: fieldId, value: value },
        });
      }
    }
    setValidateViewEditorData((prev) => ({ ...prev, [fieldId]: data }));
  };

  const onCheckFileMetaDataBtnClick = () => {
    setCurrentView('validate');
  };

  const getFormFieldValuesFromEditor = useCallback(
    (fileId: string): Record<string, unknown> => {
      return {
        ...editor?.getData(),
        ...(currentView === 'validate' ? validateViewEditorData[fileId] : {}),
      };
    },
    [currentView, editor, validateViewEditorData]
  );

  const onFileRemove = useCallback((fileData: FileData) => {
    if (isFileSelection(fileData)) {
      dispatch({ type: 'removeFileSelections', payload: [fileData.id] });
      setValidateViewErrors((prev) => {
        const { [fileData.id]: _, ...rest } = prev;
        return rest;
      });
    }
  }, []);

  const upload = useCallback(
    async (file: FileSelection) => {
      // before uploading file check if any field has option 'selectAll'
      // && if the length is more than 10
      const metaData = getFormFieldValuesFromEditor(file.id);
      for (const propertyKey in jsonSchema.properties) {
        if (
          typeof jsonSchema.properties[propertyKey] === 'object' &&
          jsonSchema.properties[propertyKey]?.options?.selectAll &&
          Array.isArray(metaData[propertyKey]) &&
          metaData[propertyKey].length > 10
        ) {
          const error = t('app.upload.hideAndSelectAllFieldError', {
            title: jsonSchema.properties[propertyKey].title,
          });
          setConditionalFilterOptionsError(error);
          console.error(error);
          return;
        }
      }

      const updateFileState = (
        newState: 'isPending' | 'isSuccess' | 'isError',
        error?: string
      ) => {
        dispatch({
          type: 'updateFileSelectionUploadState',
          payload: { id: file.id, newState, error },
        });
      };

      updateFileState('isPending');

      try {
        // readFromFileMetadata is custom property added by the portal-gateway
        // to show the readFromFileMetadata checkbox in the validate view to indicate user a field will be autoFilled from filemetadata
        // this is not needed in the api call
        if (
          metaData &&
          DOC_UPLOAD_WITH_FILE_METADATA_EXTRACTION_KEY in metaData
        ) {
          delete metaData[DOC_UPLOAD_WITH_FILE_METADATA_EXTRACTION_KEY];
        }
        await uploadFile(file, metaData);
        updateFileState('isSuccess');
        await onUploadSuccess?.();
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : 'Unknown error';
        updateFileState('isError', errorMessage);
      }
    },
    [
      getFormFieldValuesFromEditor,
      jsonSchema.properties,
      t,
      uploadFile,
      onUploadSuccess,
    ]
  );

  const onClickUpload = useCallback(async () => {
    await Promise.all(fileState.map(upload));
  }, [fileState, upload]);

  const cleanupAndClose = () => {
    dispatch({ type: 'resetFileSelections' });
    onClose();
  };

  const onBackBtnClick = () => {
    if (currentView === 'upload') {
      cleanupAndClose();
    } else {
      setCurrentView('upload');
      setValidateViewErrors({});
    }
  };

  const decideActionButtons = () => {
    const isUploadView = currentView === 'upload';
    const isMetaDataExtractionFlowEnabled = documentMetaDataExtraction?.enabled;
    const isUploadFieldValid = fileState[0] && uploadViewErrors.length === 0;
    const isMetaDataFieldValid =
      fileState[0] &&
      Object.values(validateViewErrors).every(
        (value) => Array.isArray(value) && value.length === 0
      );
    const cancelButton = (
      <Button
        variant="secondary"
        icon={<GoogleFontIcon name={isUploadView ? 'close' : 'chevron_left'} />}
        onClick={onBackBtnClick}
        disabled={!isInitial}
      >
        {t(isUploadView ? 'app.upload.cancel' : 'app.upload.back')}
      </Button>
    );

    const primaryButton = () => {
      if (!isInitial) {
        return (
          <Button
            icon={
              isPending ? (
                <SVGIcon name="upload" />
              ) : (
                <GoogleFontIcon name="check" />
              )
            }
            onClick={cleanupAndClose}
            disabled={isPending}
          >
            {t(isPending ? 'app.upload.uploading' : 'app.upload.okay')}
          </Button>
        );
      }

      if (isUploadView && isMetaDataExtractionFlowEnabled) {
        return (
          <Button
            icon={<GoogleFontIcon name="receipt_long" />}
            onClick={onCheckFileMetaDataBtnClick}
            disabled={!isUploadFieldValid || isTableModalValuesLoading}
          >
            {t('app.upload.extractMetaData.checkBtnLabel')}
          </Button>
        );
      }

      if (!isUploadView && isMetaDataExtractionFlowEnabled) {
        return (
          <Button
            icon={<GoogleFontIcon name="receipt_long" />}
            onClick={onClickUpload}
            disabled={
              !isMetaDataFieldValid ||
              isTableModalValuesLoading ||
              !!conditionalFilterOptionsError
            }
          >
            {t('app.upload.upload')}
          </Button>
        );
      }

      return (
        <Button
          icon={<SVGIcon name="upload" />}
          onClick={onClickUpload}
          disabled={
            !isUploadFieldValid ||
            isTableModalValuesLoading ||
            !!conditionalFilterOptionsError
          }
        >
          {t('app.upload.upload')}
        </Button>
      );
    };

    return (
      <>
        {cancelButton}
        {primaryButton()}
      </>
    );
  };

  return (
    <Modal
      actions={decideActionButtons()}
      contentClassName={styles['main-content']}
      modalType="action"
      opened={true}
      onClose={cleanupAndClose}
      size={960}
      title={t('app.upload.zipUploadModal.headline')}
      withinPortal={withinPortal}
    >
      {conditionalFilterOptionsError && (
        <InlineAlert
          className={
            currentView === 'upload' ? styles['alert'] : styles['warning']
          }
        >
          {conditionalFilterOptionsError}
        </InlineAlert>
      )}
      {currentView === 'upload' ? (
        <div className={styles['content']}>
          <Upload
            accept={acceptedMimeTypes}
            fileState={fileState}
            dispatch={dispatch}
            uploadFile={upload}
            maxSize={5 * 10 ** 7} // 50 MB
            documentMetaDataExtraction={documentMetaDataExtraction}
          />
          <div
            className={clsx(
              'portal-form rje-form rje-theme rje-theme--light',
              styles['editor']
            )}
          >
            <Widget editor={editor} options={{ disabled: !isInitial }} />
          </div>
        </div>
      ) : (
        <>
          <MetaDataExtractionInfo
            fileState={fileState}
            validateViewErrors={validateViewErrors}
          />

          <RenderMetaDataExtractedView
            docMetadataExtractionInfo={docMetadataExtractionInfo}
            documentMetaDataExtraction={documentMetaDataExtraction}
            fileState={fileState}
            onEditorDataChange={onMetaDataEditorChange}
            setErrors={setErrors}
            onFileRemove={onFileRemove}
            upload={upload}
          />
        </>
      )}
    </Modal>
  );
}
